// Shared 2D part-preview component.
//
// Side/front/top projection of the evaluated mesh chunks of a class
// or a single cabinet. The part the parent has selected is highlighted
// in orange; clicking any other rectangle calls `onSelect` with that
// part's id. Read-only — drag-to-edit is a follow-up that has to deal
// with rewriting underlying expressions, not implemented yet.
//
// Mounted in two places:
//   • CabinetClassEditor's Parts tab — class authoring preview at the
//     chosen preview dims.
//   • CabinetEditor right rail — per-cabinet view that reflects the
//     selected cabinet's actual W/H/D + classParams + partOverrides.

import { useMemo, useState } from "react";
import type { CabinetMeshChunk, PartDef } from "@/lib/types";

type ViewKind = "front" | "side" | "top";

const MATERIAL_FILL: Record<PartDef["materialSlot"], string> = {
  carcass: "#dec3a4",
  back: "#9a7a55",
  door: "#5e3b1f",
  shelf: "#dec3a4",
  toe: "#bba98b",
  custom: "#9aa3ad",
};

interface MeshBox {
  id: string;
  min: [number, number, number];
  max: [number, number, number];
}

export function meshBBox(chunk: CabinetMeshChunk): MeshBox | null {
  if (!chunk.vertices.length) return null;
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < chunk.vertices.length; i += 3) {
    const x = chunk.vertices[i]!, y = chunk.vertices[i + 1]!, z = chunk.vertices[i + 2]!;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }
  return { id: chunk.name, min: [minX, minY, minZ], max: [maxX, maxY, maxZ] };
}

interface Props {
  meshes: CabinetMeshChunk[];
  cabinetW: number;
  cabinetH: number;
  cabinetD: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Override SVG height (default 240px — wider editors use it as-is,
   *  narrower right-rail panels usually drop to ~180). */
  height?: number;
  /** Compact mode hides the header (label + part count) — useful in
   *  tight side panels. */
  compact?: boolean;
}

export function PartsPreview2D({
  meshes, cabinetW, cabinetH, cabinetD,
  selectedId, onSelect, height = 240, compact = false,
}: Props) {
  const [view, setView] = useState<ViewKind>("front");

  const boxes = useMemo(
    () => meshes.map(meshBBox).filter((b): b is MeshBox => b !== null),
    [meshes],
  );

  // Drawing rect in inches → SVG user units (1:1). Pad slightly so
  // outer panels don't kiss the border. SVG flips Y because origin
  // is top-left there but bottom in the cabinet frame.
  const PAD = 1;
  const margin = 2;
  const labelEvery = boxes.length <= 12;

  return (
    <section style={{
      border: "1px solid var(--line)",
      borderRadius: 4,
      padding: 8,
      background: "rgba(255,255,255,0.02)",
      display: "grid", gridTemplateRows: "auto 1fr", gap: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {!compact && <span className="label" style={{ margin: 0 }}>Preview</span>}
        <ViewToggle view={view} onChange={setView} />
        {!compact && (
          <span className="muted" style={{ fontSize: 10, flex: 1, textAlign: "right" }}>
            {boxes.length} parts · click a panel to edit
          </span>
        )}
      </div>
      <PreviewSvg
        view={view}
        boxes={boxes}
        cabinetW={cabinetW}
        cabinetH={cabinetH}
        cabinetD={cabinetD}
        selectedId={selectedId}
        onSelect={onSelect}
        pad={PAD}
        margin={margin}
        height={height}
        showLabels={labelEvery && !compact}
      />
    </section>
  );
}

function ViewToggle({ view, onChange }: { view: ViewKind; onChange: (v: ViewKind) => void }) {
  const opts: ViewKind[] = ["front", "side", "top"];
  return (
    <div style={{ display: "flex", border: "1px solid var(--line)", borderRadius: 4 }}>
      {opts.map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          style={{
            padding: "2px 8px",
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            background: view === v ? "rgba(255,170,85,0.10)" : "transparent",
            color: view === v ? "var(--accent, #ffaa55)" : "var(--fg-dim)",
            border: "none",
            cursor: "pointer",
          }}
        >{v}</button>
      ))}
    </div>
  );
}

function PreviewSvg({
  view, boxes, cabinetW, cabinetH, cabinetD,
  selectedId, onSelect, pad, margin, height, showLabels,
}: {
  view: ViewKind;
  boxes: MeshBox[];
  cabinetW: number;
  cabinetH: number;
  cabinetD: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  pad: number;
  margin: number;
  height: number;
  showLabels: boolean;
}) {
  function project(box: MeshBox): { x: number; y: number; w: number; h: number } {
    const [x0, y0, z0] = box.min;
    const [x1, y1, z1] = box.max;
    if (view === "front") return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    if (view === "side")  return { x: z0, y: y0, w: z1 - z0, h: y1 - y0 };
    /* top */              return { x: x0, y: z0, w: x1 - x0, h: z1 - z0 };
  }
  const innerW = view === "side" ? cabinetD : cabinetW;
  const innerH = view === "top"  ? cabinetD : cabinetH;
  const viewW  = innerW + 2 * margin;
  const viewH  = innerH + 2 * margin;

  // Sort so doors render on top (they're literally at z=D, in front).
  const sorted = [...boxes].sort((a, b) => {
    if (view !== "side" && view !== "top") {
      return (a.min[2] + a.max[2]) / 2 - (b.min[2] + b.max[2]) / 2;
    }
    return 0;
  });

  return (
    <svg
      viewBox={`${-margin} ${-margin} ${viewW} ${viewH}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: "100%", height, display: "block" }}
      aria-label={`${view} view`}
    >
      <rect
        x={0} y={0} width={innerW} height={innerH}
        fill="none" stroke="var(--line)" strokeWidth={0.05}
      />
      <g transform={`translate(0, ${innerH}) scale(1, -1)`}>
        {sorted.map((b) => {
          const r = project(b);
          if (r.w <= 0 || r.h <= 0) return null;
          const fill = pickFill(b.id);
          const isSelected = selectedId === b.id;
          return (
            <g key={b.id} onClick={() => onSelect(b.id)} style={{ cursor: "pointer" }}>
              <rect
                x={r.x + pad / 2}
                y={r.y + pad / 2}
                width={Math.max(0, r.w - pad)}
                height={Math.max(0, r.h - pad)}
                fill={fill}
                fillOpacity={isSelected ? 0.85 : 0.55}
                stroke={isSelected ? "var(--accent, #ffaa55)" : "rgba(0,0,0,0.6)"}
                strokeWidth={isSelected ? 0.18 : 0.05}
              >
                <title>{`${b.id} · ${r.w.toFixed(2)}″ × ${r.h.toFixed(2)}″`}</title>
              </rect>
              {showLabels && r.w > 4 && r.h > 1.5 && (
                <g transform={`translate(${r.x + r.w / 2}, ${r.y + r.h / 2}) scale(1, -1)`}>
                  <text
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={Math.min(1.2, r.h * 0.6)}
                    fill={isSelected ? "#1a1a1a" : "rgba(0,0,0,0.7)"}
                    style={{ pointerEvents: "none", fontFamily: "ui-monospace, monospace" }}
                  >
                    {b.id}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function pickFill(id: string): string {
  if (/^door/.test(id)) return MATERIAL_FILL.door;
  if (/^shelf/.test(id)) return MATERIAL_FILL.shelf;
  if (id === "back") return MATERIAL_FILL.back;
  if (id === "toe_kick") return MATERIAL_FILL.toe;
  return MATERIAL_FILL.carcass;
}
